import axios, { AxiosError, AxiosRequestConfig } from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
});

const refreshClient = axios.create({
  baseURL: BASE_URL,
});

// ─── REQUEST INTERCEPTOR ─────────────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  config.headers = config.headers ?? {};
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── REFRESH TOKEN LOGIKASI ──────────────────────────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else if (token) p.resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  window.location.href = "/auth";
};

// ─── SERVER DOWN DETEKTORI ───────────────────────────────────────────
// Tarmoq xatosi (backend umuman javob bermadi) yoki 502/503/504 —
// bular foydalanuvchi kodidagi xato emas, server/hosting muammosi.
// Global event orqali butun ilovaga "server ishlamayapti" ekranini
// ko'rsatishga signal beramiz.
const SERVER_DOWN_STATUSES = [502, 503, 504];
const notifyServerDown = (err: AxiosError) => {
  const status = err.response?.status;
  const isNetworkError = !err.response;
  if (isNetworkError || (status && SERVER_DOWN_STATUSES.includes(status))) {
    window.dispatchEvent(new CustomEvent("app:server-down"));
  }
};

// ─── RESPONSE INTERCEPTOR ────────────────────────────────────────────
api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    notifyServerDown(err);

    const originalRequest = err.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // 401 emas yoki config yo'q — qaytar
    if (err.response?.status !== 401 || !originalRequest) {
      return Promise.reject(err);
    }

    // /auth/login yoki /auth/refresh o'zi 401 bersa — to'g'ridan-to'g'ri logout
    const url = originalRequest.url || "";
    if (url.includes("/auth/login") || url.includes("/auth/refresh")) {
      if (url.includes("/auth/refresh")) forceLogout();
      return Promise.reject(err);
    }

    // Allaqachon retry qilingan bo'lsa — chiqarib yubor
    if (originalRequest._retry) {
      forceLogout();
      return Promise.reject(err);
    }

    // Boshqa request refresh qilayotgan bo'lsa — queue'ga qo'sh
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          return api(originalRequest);
        })
        .catch((e) => Promise.reject(e));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      isRefreshing = false;
      forceLogout();
      return Promise.reject(err);
    }

    try {
      // 👇 Backend formatga moslab — { refresh_token }
      const res = await refreshClient.post("/auth/refresh", {
        refresh_token: refreshToken,
      });

      const newAccess = res.data?.access_token;
      const newRefresh = res.data?.refresh_token;

      if (!newAccess) {
        throw new Error("No access_token in refresh response");
      }

      localStorage.setItem("token", newAccess);
      if (newRefresh) {
        localStorage.setItem("refresh_token", newRefresh);
      }

      processQueue(null, newAccess);

      // Asl request'ni yangi token bilan qaytadan yubor
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      }
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─── API ENDPOINTS (eski qoldi, hech narsa o'zgarmadi) ──────────────

export const sessionsAPI = {
  start: (dto: {
    mode: "topic" | "ticket" | "exam" | "random";
    topicId?: number;
    ticketId?: number;
    count?: number;
  }) => api.post("/test-sessions", dto),

  finish: (
    sessionId: string,
    timeSpent: number,
    answers: { questionId: number; answerIndex: number | null }[],
  ) => api.patch(`/test-sessions/${sessionId}/finish`, { timeSpent, answers }),
};

export const duelAPI = {
  start: (data: { difficulty?: string; totalQuestions?: number }) =>
    api.post("/duel/start", data),
  submitAnswer: (
    sessionId: string,
    data: { questionId: number; choiceIndex: number },
  ) => api.post(`/duel/${sessionId}/answer`, data),
  finish: (sessionId: string) => api.post(`/duel/${sessionId}/finish`),
  getStats: () => api.get("/stats/me"),
  updateStats: (result: "win" | "loss" | "draw") =>
    api.post("/stats/update", { result }),
};

export const adminAPI = {
  uploadPhoto: (ticketId: number, fileName: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(
      `/admin/questions/upload-photo?ticketId=${ticketId}&fileName=${fileName}`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
  },
};

export const timeAttackAPI = {
  start: (data: any) => api.post("/time-attack/start", data),
  nextQuestion: (sessionId: string) =>
    api.get(`/time-attack/${sessionId}/next`),
  submitAnswer: (sessionId: string, data: any) =>
    api.post(`/time-attack/${sessionId}/answer`, data),
  finish: (sessionId: string, timeRemaining: number) =>
    api.post(`/time-attack/${sessionId}/finish`, { timeRemaining }),
  leaderboard: (limit = 10) =>
    api.get(`/time-attack/leaderboard?limit=${limit}`),
};

export const authAPI = {
  signIn: (emailOrPhone: string, password: string) =>
    api.post("/auth/login", { emailOrPhone, password }),

  signUp: (
    name: string,
    email: string,
    password: string,
    confirm_password: string,
    role?: string,
  ) =>
    api.post("/users/signup", {
      name,
      email,
      password,
      confirm_password,
      role,
    }),

  telegramLogin: (payload: any) => api.post("/auth/telegram", payload),

  telegramOidcLogin: (id_token: string, role: "user" | "teacher" = "user") =>
    api.post("/auth/telegram-oidc", { id_token, role }),

  // Mobil (native SDK) uchun mo'ljallangan endpoint — webdan Google
  // Identity Services orqali test qilish mumkin (bir xil id_token shakli)
  googleMobileLogin: (id_token: string, role: "user" | "teacher" = "user") =>
    api.post("/auth/google-mobile", { id_token, role }),

  logout: () => api.post("/auth/logout"),
};

export const marathonAPI = {
  getQuestions: (lang = "uz", limit = 10, exclude: number[] = []) => {
    const params = new URLSearchParams({
      lang,
      limit: limit.toString(),
      random: "true",
    });
    if (exclude.length > 0) params.append("exclude", exclude.join(","));
    return api.get(`/questions/marathon?${params.toString()}`);
  },
};

export const murakkabAPI = {
  getQuestions: (lang = "uz", limit = 20, exclude: number[] = []) => {
    const params = new URLSearchParams({
      lang,
      limit: limit.toString(),
      random: "true",
    });
    if (exclude.length > 0) params.append("exclude", exclude.join(","));
    return api.get(`/questions/murakkab?${params.toString()}`);
  },
};

export const sonliAPI = {
  getQuestions: (lang = "uz", limit = 20, exclude: number[] = []) => {
    const params = new URLSearchParams({
      lang,
      limit: limit.toString(),
      random: "true",
    });
    if (exclude.length > 0) params.append("exclude", exclude.join(","));
    return api.get(`/questions/sonli?${params.toString()}`);
  },
};

export const userAnswersAPI = {
  saveMistakes: (questionIds: number[]) =>
    api.post("/user-question-answers", { questionIds }),
  getCount: () => api.get("/user-question-answers/mistakes-count"),
  getMistakes: (lang: string = "uz") =>
    api.get(`/user-question-answers/mistakes?lang=${lang}`),
  markAsCorrect: (questionId: number) =>
    api.patch(`/user-question-answers/${questionId}/correct`),
  getMyMistakeCountForQuestion: (questionId: number) =>
    api.get<{ count: number }>(
      `/user-question-answers/questions/${questionId}/my-mistake-count`,
    ),
};

export const topicsAPI = {
  getAll: (page = 1, limit = 9, lang = "uz") =>
    api.get(`/topics?page=${page}&limit=${limit}&lang=${lang}`),
  getById: (id: number) => api.get(`/topics/${id}`),
  getQuestions: (id: number, lang = "uz") =>
    api.get(`/topics/${id}/questions?lang=${lang}`),
};

export const ticketsAPI = {
  getAll: (page = 1, limit = 12) =>
    api.get(`/tickets?page=${page}&limit=${limit}`),
  getById: (id: number) => api.get(`/tickets/${id}`),
  getQuestions: (id: number, lang = "uz") =>
    api.get(`/tickets/${id}/questions?lang=${lang}`),
};

export const usersAPI = {
  getProgress: () => api.get("/users/progress"),
  getFavorites: () => api.get("/users/favorites"),
  toggleFavorite: (questionId: number) =>
    api.patch(`/users/favorites/${questionId}`),
  getMe: () => api.get("/users/me"),
  updateRole: (role: string) => api.patch("/users/role", { role }),
  linkTelegram: (payload: any) => api.post("/users/link-telegram", payload),
};

export const friendDuelAPI = {
  invite: (data: { uniqueCode: string; questionCount: number }) =>
    api.post("/friend-duel/invite", data),
  cancel: (inviteId: string) => api.post(`/friend-duel/cancel/${inviteId}`),
  accept: (inviteId: string) => api.post(`/friend-duel/accept/${inviteId}`),
  decline: (inviteId: string) => api.post(`/friend-duel/decline/${inviteId}`),
  getPending: () => api.get("/friend-duel/pending"),
};

export const signsAPI = {
  getCategories: () => api.get("/signs/categories"),
  getByCategory: (categoryId: string, lang: "uz" | "ru" | "en" = "uz") =>
    api.get(`/signs/${categoryId}`, { params: { lang } }),
};

export const shpargalkaAPI = {
  getAll: () => api.get("/shpargalka"),
  getById: (id: string) => api.get(`/shpargalka/${id}`),
};

export const aiUstozAPI = {
  getInsights: (lang: "oz" | "uz" | "ru" = "uz") =>
    api.get(`/ai/ustoz/insights?lang=${lang}`),
  sendMessage: (message: string, conversationId?: string | null) =>
    api.post("/ai/ustoz/chat", { message, conversationId }),
  getConversation: (conversationId: string) =>
    api.get(`/ai/ustoz/conversations/${conversationId}`),
  explainQuestion: (questionId: number, lang: "oz" | "uz" | "ru" = "uz") =>
    api.post<{ explanation: string; cached: boolean }>(
      `/ai/ustoz/questions/${questionId}/explain?lang=${lang}`,
    ),
};

export const notificationsAPI = {
  getAll: () => api.get("/notifications"),
  getById: (id: number) => api.get(`/notifications/${id}`),
};

export const questionReportsAPI = {
  create: (data: {
    questionId: number;
    types: ("wrong_answer" | "typo" | "comment")[];
    comment?: string;
  }) => api.post("/question-reports", data),
};

export const videosAPI = {
  getAll: () => api.get("/videos"),
  getById: (id: number) => api.get(`/videos/${id}`),
};

export const statsAPI = {
  clearHistory: () => api.delete("/stats/history"),
};

export const teacherGroupsAPI = {
  // Ustoz
  createGroup: (data: { name: string; max_students?: number }) =>
    api.post("/teacher-groups", data),

  getMyGroups: () => api.get("/teacher-groups/my-groups"),

  inviteStudent: (groupId: string, unique_code: string) =>
    api.post(`/teacher-groups/${groupId}/invite`, { unique_code }),

  deleteGroup: (groupId: string) => api.delete(`/teacher-groups/${groupId}`),

  getStudentProgress: (groupId: string, studentId: string, limit = 20) =>
    api.get(
      `/teacher-groups/${groupId}/members/${studentId}/progress?limit=${limit}`,
    ),

  // O'quvchi
  getMyInvitations: () => api.get("/teacher-groups/invitations"),

  respondInvitation: (id: string, accept: boolean) =>
    api.post(`/teacher-groups/invitations/${id}/respond`, { accept }),

  getStudentGroups: () => api.get("/teacher-groups/my-student-groups"),
};

export default api;
